<template>
  <div class="syuugyou-basyo edit-item" v-if="editingData">
    <div class="head"><b>部課・就業場所</b></div>

    <div class="data">
      <div class="item">
        <p v-if="mode===OptionConst.KOYOUKEIYAKU_EDIT_MODE.MODIFY && editingData.koyoukeiyaku_is_used">※既に打刻・申請が存在しているため、部課・就業場所の変更はできません。</p>
        <b>部課（PX上の管理区分）</b>
        <div class="data">
          <select-box v-if="bukaOptions && selectedBukaId" :options="bukaOptions" v-model="selectedBukaId"
            @update:modelValue="updateBuka" :disabled="mode===OptionConst.KOYOUKEIYAKU_EDIT_MODE.MODIFY && editingData.koyoukeiyaku_is_used"></select-box>
          <p v-else>部課が取得できません。</p>
          <p class="current-data" v-if="lastBukaName">{{ lastBukaName }}</p>
        </div>
      </div>

      <div class="item">
        <b>就業場所</b>
        <div class="data">
          <select-box v-if="filteredSyuugyouBasyoList && editingData.mst_syuugyou_basyo_id"
            :options="filteredSyuugyouBasyoList" v-model="editingData.mst_syuugyou_basyo_id"
            @update:modelValue="updateSyuugyouBasyo" :disabled="mode===OptionConst.KOYOUKEIYAKU_EDIT_MODE.MODIFY && editingData.koyoukeiyaku_is_used"></select-box>
          <p v-else class="danger-text">就業場所一覧を取得できません。<br>マスタ管理画面より当該部課に就業場所が設定されているか確認してください。</p>
          <p class="input-error" v-if="getError(editingData, 'mst_syuugyou_basyo_id')">{{ getError(editingData, 'mst_syuugyou_basyo_id') }}</p>
          <p class="current-data" v-if="lastSyuugyouBasyoName">{{ lastSyuugyouBasyoName }}</p>
        </div>
      </div>

      <div class="item info" v-if="selectedSyuugyouBasyo">
        <p>{{ selectedSyuugyouBasyo.syuugyou_basyo_address }}</p>
        <p>{{ selectedSyuugyouBasyo.syuugyou_basyo_tel }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useForm } from '@/composable/form/form';
import OptionConst from '@/const/Option';
import { KoyouKeiyakuDetail } from '@/types/KoyouKeiyakuDetail';
import { defineComponent, onMounted, ref, Ref } from 'vue'
import { useSyuugyouBasyoInit } from '@/composable/syuubyoubasyo/init';
import { useMstBukaInit } from '@/composable/buka/init';
import { cloneDeep } from 'lodash';
import { BaseOptionWithoutCode } from "@/types/Option";

export default defineComponent({
  props: {
    editingData: {
      type: Object as () => KoyouKeiyakuDetail | null,
      require: true,
      default: null
    },
    currentData: {
      type: Object as () => KoyouKeiyakuDetail | null,
      require: true,
      default: null
    },
    disabled: {
      type: Object as () => boolean,
      default: false
    },
    mode: {
      // モード 新規新規(create)／既存契約からの新規作成(from)／編集(edit)／承認後の編集（modify）の４モードを想定
      type: String,
      require: true,
      default: null
    }
  },
  setup(props) {
    const { getError } = useForm();
    const { syuugyouBasyoOptions, selectedSyuugyouBasyo, syuugyouBasyoList, getAllSyuugyouBasyoList, getAllSyuugyouBasyoOptions } = useSyuugyouBasyoInit();
    const { allBukaOptions, bukaOptions, getAllBukaOptions, getBukaOptionsBySyozokuId } = useMstBukaInit();

    // 選択された部課ID
    const selectedBukaId: Ref<Number | null> = ref(null);
    // 部課で絞られた就業場所リスト
    const filteredSyuugyouBasyoList: Ref<BaseOptionWithoutCode[] | null> = ref(null);

    // 参照している雇用契約書の就業場所の住所とTEL
    const lastSyuugyouBasyoName: Ref<string> = ref("");
    const lastBukaName: Ref<string> = ref("");

    onMounted(async () => {
      await getAllSyuugyouBasyoList();
      await getAllSyuugyouBasyoOptions();
      await getAllBukaOptions();

      // 所属IDで部課を絞る
      if (props.editingData?.syozoku_id) {
        await getBukaOptionsBySyozokuId(props.editingData.syozoku_id);
      }

      // 選択された部課の初期設定
      if (syuugyouBasyoOptions.value && syuugyouBasyoList.value) {
        filteredSyuugyouBasyoList.value = cloneDeep(syuugyouBasyoOptions.value);
        if (props.editingData && props.editingData.mst_syuugyou_basyo_id) {
          let findBukaId = bukaOptions.value.find((buka) => buka.val === props.editingData?.syuugyou_basyos?.mst_buka_id)?.val;
          if (typeof findBukaId !== 'undefined') {
            selectedBukaId.value = findBukaId;
          }
          // DBに登録された就業場所IDが、本来その所属会社の就業場所に属していない場合などの処理
          else {
            // 念の為、就業場所IDをnullにする
            props.editingData.mst_syuugyou_basyo_id = null;
            if (bukaOptions.value) {
              // 所属IDで絞られた部課オプションのindex0を選択された部課に指定
              let buka = bukaOptions.value?.[0];
              if (typeof buka !== 'undefined') {
                selectedBukaId.value = buka.val;
                // 選択された部課の中の就業場所で最初の1件を就業場所IDに指定
                let syuubyoubasyoId = syuugyouBasyoList.value.find((list) => list.mst_buka_id === selectedBukaId.value)?.mst_syuugyou_basyo_id;
                if (props.editingData && syuubyoubasyoId) {
                  props.editingData.mst_syuugyou_basyo_id = syuubyoubasyoId;
                }
              }
            }
          }
        }
        // 入社タイプなどで就業場所IDが未設定の場合
        else if (bukaOptions.value && (!props.editingData || !props.editingData.mst_syuugyou_basyo_id)) {
          // 所属IDで絞られた部課オプションのindex0を選択された部課に指定
          let buka = bukaOptions.value?.[0];
          if (typeof buka !== 'undefined') {
            selectedBukaId.value = buka.val;
            // 選択された部課の中の就業場所で最初の1件を就業場所IDに指定
            let syuubyoubasyoId = syuugyouBasyoList.value.find((list) => list.mst_buka_id === selectedBukaId.value)?.mst_syuugyou_basyo_id;
            if (props.editingData && syuubyoubasyoId) {
              props.editingData.mst_syuugyou_basyo_id = syuubyoubasyoId;
            }
          }
        }
      }

      // 部課で就業場所オプションリストを設定
      if (filteredSyuugyouBasyoList.value && selectedBukaId.value) {
        await filterSyuugyouBasyoList(Number(selectedBukaId.value));
      }
      // 画面表示用の選択された就業場所データを更新
      await changeSelectedSyuugyouBasyo();

      // 参照している雇用契約書の就業場所の住所とTELを取得
      if (props.currentData && props.currentData.mst_syuugyou_basyo_id && syuugyouBasyoList.value && bukaOptions.value) {
        let syuubyoubasyo = syuugyouBasyoList.value.find((list) => list.mst_syuugyou_basyo_id === props.currentData?.mst_syuugyou_basyo_id);
        if (typeof syuubyoubasyo !== 'undefined') {
          lastSyuugyouBasyoName.value = syuubyoubasyo.syuugyou_basyo_name;
          // 参照データの就業場所IDが、本来その所属会社の就業場所に含まれていない場合を想定して、全就業場所リストから部課ID、名前を取得する
          let bukaId = syuugyouBasyoList.value.find((list) => list.mst_buka_id === syuubyoubasyo?.mst_buka_id)?.mst_buka_id;
          if (typeof bukaId !== 'undefined') {
            // 絞られていない全部課オプションから取得する
            let buka = allBukaOptions.value.find((buka) => buka.val === bukaId);
            if (typeof buka !== 'undefined') {
              lastBukaName.value = buka.label;
            }
          }
        }
      }
    })  // onMounted

    // 部課が変更された場合の処理
    const updateBuka = (val: BaseOptionWithoutCode) => {
      // 就業場所オプションリストを更新
      if (filteredSyuugyouBasyoList.value && selectedBukaId.value && props.editingData) {
        filterSyuugyouBasyoList(Number(selectedBukaId.value));
        // 就業場所セレクトボックスのselectedを指定
        if(filteredSyuugyouBasyoList.value.length > 0) {
          props.editingData.mst_syuugyou_basyo_id = Number(filteredSyuugyouBasyoList.value[0].val);
        } else {
          props.editingData.mst_syuugyou_basyo_id = null;
        }
      }
      changeSelectedSyuugyouBasyo();
    }

    // 就業場所が変更された場合の処理
    const updateSyuugyouBasyo = (val: BaseOptionWithoutCode) => {
      changeSelectedSyuugyouBasyo();
    }

    /**
     * 部課で就業場所リストを絞り込む
     */
    const filterSyuugyouBasyoList = async (selectedBukaId: number) => {
      if (!syuugyouBasyoList.value) {
        return
      }
      let initList = cloneDeep(syuugyouBasyoList.value);

      // 全就業場所リストを部課でフィルターする
      initList = initList.filter((list) => list.mst_buka_id === selectedBukaId);

      // select-boxのoptionsで使用するfilteredSyuugyouBasyoListに{val,label}形式で反映する
      filteredSyuugyouBasyoList.value = [];
      initList.forEach(list => {
        filteredSyuugyouBasyoList.value?.push({ val: Number(list.mst_syuugyou_basyo_id), label: String(list.syuugyou_basyo_name) });
      })
    }

    /**
     * 画面表示用の住所とTELを返す
     */
    const getSyuugyouBasyoLabel = () => {
      if (!syuugyouBasyoList.value || !props.editingData || !props.editingData.mst_syuugyou_basyo_id) {
        return
      }
      else {
        let select = syuugyouBasyoList.value.find((list) => list.mst_syuugyou_basyo_id === props.editingData?.mst_syuugyou_basyo_id)
        if (typeof select !== 'undefined') {
          selectedSyuugyouBasyo.value = cloneDeep(select);
        }
      }
    }

    /**
     * 画面表示用の選択された就業場所の情報を更新する
     */
    const changeSelectedSyuugyouBasyo = async () => {
      if (syuugyouBasyoList.value && props.editingData && props.editingData.mst_syuugyou_basyo_id) {
        let select = syuugyouBasyoList.value.find((list) => list.mst_syuugyou_basyo_id === props.editingData?.mst_syuugyou_basyo_id)
        if (typeof select !== 'undefined') {
          selectedSyuugyouBasyo.value = cloneDeep(select);
        }
      } else {
        selectedSyuugyouBasyo.value = null
      }
      getSyuugyouBasyoLabel();
    }

    return {
      OptionConst,
      getError,
      syuugyouBasyoList,
      selectedSyuugyouBasyo,
      bukaOptions,
      selectedBukaId,
      syuugyouBasyoOptions,
      filteredSyuugyouBasyoList,
      updateBuka,
      updateSyuugyouBasyo,
      lastSyuugyouBasyoName,
      lastBukaName
    }
  }
})
</script>

<style lang="scss" scoped>
@import '@scss/components/koyoukeiyaku.scss';
@import '@scss/components/koyoukeiyaku-edit.scss';

.info {
  margin-left: 8px;
}
</style>
